import { forwardRef } from 'react';
import { useCheckbox } from 'react-aria';
import { useToggleState } from 'react-stately';

import { useObjectRef } from '@react-aria/utils';
import { cn } from '@/lib/utils';

export interface CheckboxProps {
  label?: string;
  description?: string;
  error?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      checked,
      onChange,
      disabled,
      className,
      name,
      ...props
    },
    forwardedRef
  ) => {
    const ref = useObjectRef(forwardedRef);
    const state = useToggleState({
      isSelected: checked,
      onChange,
      isDisabled: disabled,
    });

    const { inputProps, labelProps } = useCheckbox(
      {
        isSelected: checked,
        onChange,
        isDisabled: disabled,
        ...props,
      },
      state,
      ref
    );

    return (
      <div className='flex items-start space-x-3'>
        <div className='flex h-5 items-center'>
          <input
            {...inputProps}
            ref={ref}
            name={name}
            type='checkbox'
            className={cn(
              'h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary',
              error && 'border-red-500 focus:ring-red-500',
              className
            )}
          />
        </div>
        <div className='flex flex-col'>
          {label && (
            <label
              {...labelProps}
              className='text-sm font-medium text-gray-700'
            >
              {label}
            </label>
          )}
          {description && (
            <p className='text-sm text-gray-500'>{description}</p>
          )}
          {error && <p className='text-sm text-red-500'>{error}</p>}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
